import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * Custom hook for managing CRM lead profile editing
 */
export function useCrmLeadProfile(leadId, lead, companies, refetch) {
  const navigate = useNavigate();
  
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [profileFormData, setProfileFormData] = useState({});
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [formData, setFormData] = useState({});
  const [saving, setSaving] = useState(false);
  const [selectedCompanyLocations, setSelectedCompanyLocations] = useState([]);

  // Initialize form data when lead changes
  const initFormData = (leadData) => {
    setFormData(leadData || {});
    setProfileFormData(leadData || {});
    if (leadData?.company_id) {
      const company = companies.find(c => c.id === leadData.company_id);
      setSelectedCompanyLocations(company?.locations || []);
    }
  };

  // Handle profile company change for inline edit
  const handleProfileCompanyChange = (companyId) => {
    setProfileFormData({ ...profileFormData, company_id: companyId, location_id: '' });
    const company = companies.find(c => c.id === companyId);
    setSelectedCompanyLocations(company?.locations || []);
  };

  // Start editing profile
  const startEditingProfile = () => {
    setProfileFormData({ ...lead });
    if (lead?.company_id) {
      const company = companies.find(c => c.id === lead.company_id);
      setSelectedCompanyLocations(company?.locations || []);
    }
    setIsEditingProfile(true);
  };

  // Cancel editing profile
  const cancelEditingProfile = () => {
    setProfileFormData({ ...lead });
    setIsEditingProfile(false);
  };

  // Save profile changes (inline edit)
  const handleSaveProfile = async () => {
    if (!profileFormData.first_name?.trim()) { 
      toast.error('First name is required'); 
      return; 
    }
    setSaving(true);
    try {
      const payload = { ...profileFormData };
      Object.keys(payload).forEach(k => { 
        if (payload[k] === '' || payload[k] === 'none') payload[k] = null; 
      });
      payload.first_name = profileFormData.first_name;
      await axios.put(`${API}/leads/${leadId}`, payload);
      toast.success('Profile updated');
      setIsEditingProfile(false);
      refetch();
    } catch (error) { 
      toast.error(error.response?.data?.detail || 'Failed to update profile'); 
    } finally { 
      setSaving(false); 
    }
  };

  // Quick toggle for DNC/DNE/Bad Number
  const handleToggleDncDne = async (field, newValue, setLead) => {
    try {
      await axios.put(`${API}/leads/${leadId}`, { [field]: newValue });
      // Update local state directly
      if (setLead) {
        setLead(prev => prev ? { ...prev, [field]: newValue } : prev);
      }
      setProfileFormData(prev => ({ ...prev, [field]: newValue }));
      const labels = {
        dnc: 'Do Not Call',
        dne: 'Do Not Email',
        bad_number: 'Bad Number'
      };
      toast.success(`Lead ${newValue ? 'marked as' : 'removed from'} ${labels[field] || field}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update preference');
    }
  };

  // Handle company change for edit dialog
  const handleCompanyChange = (companyId) => {
    setFormData({ ...formData, company_id: companyId, location_id: '' });
    const company = companies.find(c => c.id === companyId);
    setSelectedCompanyLocations(company?.locations || []);
  };

  // Update lead via edit dialog
  const handleUpdate = async () => {
    if (!formData.first_name?.trim()) { 
      toast.error('First name required'); 
      return; 
    }
    setSaving(true);
    try {
      const payload = { ...formData };
      Object.keys(payload).forEach(k => { 
        if (payload[k] === '' || payload[k] === 'none') payload[k] = null; 
      });
      payload.first_name = formData.first_name;
      await axios.put(`${API}/leads/${leadId}`, payload);
      toast.success('Lead updated');
      setEditDialogOpen(false);
      refetch();
    } catch (error) { 
      toast.error(error.response?.data?.detail || 'Failed'); 
    } finally { 
      setSaving(false); 
    }
  };

  // Delete lead
  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/leads/${leadId}`);
      toast.success('Lead deleted');
      navigate('/admin/leads');
    } catch { 
      toast.error('Failed to delete lead'); 
    }
  };

  return {
    isEditingProfile,
    setIsEditingProfile,
    profileFormData,
    setProfileFormData,
    editDialogOpen,
    setEditDialogOpen,
    deleteDialogOpen,
    setDeleteDialogOpen,
    formData,
    setFormData,
    saving,
    selectedCompanyLocations,
    initFormData,
    handleProfileCompanyChange,
    startEditingProfile,
    cancelEditingProfile,
    handleSaveProfile,
    handleToggleDncDne,
    handleCompanyChange,
    handleUpdate,
    handleDelete,
  };
}

export default useCrmLeadProfile;
